import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconBrandGoogleFilled, IconShieldLock } from "@tabler/icons-react";

import { AsiLogo } from "../components/AsiLogo";
import { saveAdminSession } from "../lib/adminAuth";

const BASE_URL = import.meta.env.VITE_API_URL ?? "";

/**
 * Platform Admin sign-in · service provider (ASI Builder) only.
 *
 * Production path · "Sign in with Google" → the API's
 * /platform-admin/auth/google/start flow, restricted to
 * @asi-builder.com Workspace accounts (hd claim enforced server-side).
 * On success the callback redirects back here with the JWT in the URL
 * fragment, which this page captures and stores.
 *
 * Local dev path · dev-login form (NODE_ENV=development only on the
 * API side) issues a JWT without Google.
 */
export function AdminLogin() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devEmail, setDevEmail] = useState("daphne@asi-builder.com");
  const [devName, setDevName] = useState("Daphne");

  // Capture the OAuth callback fragment · #token=…&expiresAt=…&name=…
  useEffect(() => {
    if (!window.location.hash.startsWith("#token=") && !window.location.hash.includes("token=")) return;
    const params = new URLSearchParams(window.location.hash.slice(1));
    const token = params.get("token");
    if (!token) return;
    saveAdminSession({
      token,
      expiresAt: params.get("expiresAt") ?? "",
      user: {
        email: params.get("email") ?? "",
        name: params.get("name") ?? "",
        role: params.get("role") ?? "ADMIN",
      },
    });
    // Scrub the fragment from the address bar, then enter the console.
    window.history.replaceState(null, "", window.location.pathname);
    navigate("/admin", { replace: true });
  }, [navigate]);

  const devLogin = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await fetch(`${BASE_URL}/platform-admin/auth/dev-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: devEmail,
          name: devName,
          googleSubject: `dev-${devEmail}`,
        }),
      });
      const body = (await res.json()) as {
        token?: string;
        expiresAt?: string;
        user?: { id: string; email: string; name: string; role: string };
        error?: { message: string };
      };
      if (!res.ok || !body.token || !body.user) {
        setError(body.error?.message ?? `Dev login failed (${res.status})`);
        return;
      }
      saveAdminSession({
        token: body.token,
        expiresAt: body.expiresAt ?? "",
        user: body.user,
      });
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-1 p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-3">
          <AsiLogo variant="mark" size={60} />
          <div className="text-center">
            <div className="flex items-center justify-center gap-1.5 text-base font-extrabold text-brand-charcoal">
              <IconShieldLock size={18} className="text-brand-green-dark" />
              ASI <span className="text-brand-green-dark">Builder</span>
              <span className="ml-1 text-text-secondary font-medium">
                · Platform Admin
              </span>
            </div>
            <div className="text-[11px] text-text-secondary">
              Service provider console · asi-builder.com accounts only
            </div>
          </div>
        </div>

        <div className="space-y-4 rounded-xl border border-border bg-surface-2 p-5 shadow-sm">
          <a
            href={`${BASE_URL}/platform-admin/auth/google/start`}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-surface-0 py-2.5 text-sm font-medium hover:bg-surface-1"
          >
            <IconBrandGoogleFilled size={16} />
            Sign in with Google Workspace
          </a>
          <div className="text-center text-[10px] text-text-secondary">
            Restricted to @asi-builder.com · first admin auto-activates,
            later admins need activation by an existing admin
          </div>

          {import.meta.env.DEV && (
            <>
              <div className="flex items-center gap-2 text-[10px] text-text-secondary">
                <div className="h-px flex-1 bg-border" />
                local dev only
                <div className="h-px flex-1 bg-border" />
              </div>
              <label className="block text-xs">
                <span className="mb-1 block font-medium">Workspace email</span>
                <input
                  type="email"
                  value={devEmail}
                  onChange={(e) => setDevEmail(e.target.value)}
                  className="w-full rounded-lg border border-border bg-surface-0 px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-xs">
                <span className="mb-1 block font-medium">Display name</span>
                <input
                  value={devName}
                  onChange={(e) => setDevName(e.target.value)}
                  className="w-full rounded-lg border border-border bg-surface-0 px-3 py-2 text-sm"
                />
              </label>
              {error && (
                <div className="rounded-lg border border-red-300 bg-red-50 p-2.5 text-xs text-red-800">
                  {error}
                </div>
              )}
              <button
                onClick={devLogin}
                disabled={busy}
                className="w-full rounded-lg bg-text-primary py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                {busy ? "Signing in…" : "Dev sign-in (no Google)"}
              </button>
            </>
          )}
        </div>

        <div className="mt-4 text-center text-[11px] text-text-secondary">
          Client estate user?{" "}
          <a href="/login" className="underline">
            Sign in here
          </a>
        </div>
      </div>
    </div>
  );
}
